import { z } from "zod";

import { createFindBatcher } from "./createFindBatcher";
import type { RemoteLookupProvider } from "./providers";
import type { FindFetcher, FindParams, FindResult } from "./types";

export function createFindFetcher<T extends string = string, Id = unknown, Item = unknown, Filters = unknown>(
  type: T,
  provider: RemoteLookupProvider<Id, Item, Filters>
): FindFetcher<Id, Item> {
  const paramsSchema = z.object({
    app_group_id: z.string(),
    id: provider.validators.id.nullable().optional(),
  });
  const resultSchema = z.object({
    item: provider.validators.item.nullable(),
  }) as z.ZodType<FindResult<Item>>; // Type assertion needed to satisfy non-strict typescript.
  const batchersByAppGroupId = new Map<string, ReturnType<typeof createFindBatcher<T, Id, Item>>>();
  return async (signal: AbortSignal, params: FindParams<Id>): Promise<FindResult<Item>> => {
    const parsedParams = paramsSchema.parse(params);

    // Short-circuit if no id is provided.
    if (parsedParams.id === null || parsedParams.id === undefined) {
      return { item: null };
    }

    let batcher = batchersByAppGroupId.get(parsedParams.app_group_id);
    if (!batcher) {
      batcher = createFindBatcher(parsedParams.app_group_id, type, provider);
      batchersByAppGroupId.set(parsedParams.app_group_id, batcher);
    }

    // Create a promise that rejects if the signal is aborted. We can't thread
    // the signal through to actually cancel the batched request since it could
    // also be fetching other non-cancelled ids.
    const abortPromise = new Promise<never>((_resolve, reject) => {
      signal.throwIfAborted();
      signal.addEventListener(
        "abort",
        () => {
          reject(signal.reason);
        },
        { once: true }
      );
    });

    const result = await Promise.race([abortPromise, batcher.fetch(parsedParams.id)]);
    return resultSchema.parse(result);
  };
}
